import React, { Component } from 'react'
import { RefreshCw, Home } from 'lucide-react'
import type { ErrorInfo, ReactNode } from 'react'
import { captureComponentError } from '../lib/telemetry'

const SUPPORT_EMAIL =
  (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined)?.trim() ||
  'support@odonboard.com'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export default class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null, errorInfo: null }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureComponentError(error, errorInfo, 'ErrorBoundary')
    this.setState({ errorInfo })
  }

  private handleRefresh = () => window.location.reload()
  private handleGoHome = () => { window.location.href = '/' }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand shadow-[0_4px_20px_rgba(31,42,68,0.18)]">
              <span className="text-xl font-bold text-white">OD</span>
            </div>

            <h1 className="mb-3 text-2xl font-semibold tracking-tight text-foreground">
              Something went wrong
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
              An unexpected error occurred. This has been logged — try
              refreshing or returning to the dashboard.
            </p>

            <div className="flex flex-col items-center gap-3">
              <button
                onClick={this.handleRefresh}
                className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-brand px-5 py-2.5 text-sm font-medium text-white shadow-[0_1px_3px_rgba(31,42,68,0.25)] transition-all duration-150 hover:bg-brand-hover"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh page
              </button>
              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-border bg-white px-5 py-2.5 text-sm font-medium text-foreground shadow-sm transition-all duration-150 hover:bg-panel"
              >
                <Home className="h-4 w-4" />
                Return to dashboard
              </button>
            </div>

            {this.state.error && (
              <details className="mt-8 rounded-xl border border-border bg-white p-4 text-left">
                <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                  Error details
                </summary>
                <p className="mt-3 text-xs font-medium text-foreground break-words">
                  {this.state.error.message}
                </p>
                {this.state.errorInfo?.componentStack && (
                  <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words text-meta leading-relaxed text-muted-foreground">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}

            <p className="mt-6 text-xs text-muted-foreground">
              Need help?{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-medium text-brand hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
